import { findFieldIndicator } from "@utils/fieldIndicator";

import { ReviewItemFieldsFragment } from "@api/graphql/types-and-hooks";
import { anchorOf, domAnchorOf } from "@components/QaReview/hooks/useReviewAnchors";
import { useReviewSession } from "@components/QaReview/hooks/useReviewSession";
import { useIsReviewItemListed, viewListing } from "@components/QaReview/hooks/useReviewView";

/** Scroll the report so the field's suggestion indicator (or the field itself) is centered. */
export const scrollToFieldIndicator = (fieldId: string): void => {
  if (!fieldId) return;

  findFieldIndicator(fieldId)?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
};

interface GoToItemOptions {
  /** Scroll and activate without switching the review view, even if the item is not listed in it. */
  keepView?: boolean;
}

export const useGoToItem = () => {
  const { dispatch } = useReviewSession();
  const isListed = useIsReviewItemListed();

  // Suggestions use the unnormalized anchor so the active field matches what the field's own
  // indicator computes.
  return (item: ReviewItemFieldsFragment, { keepView = false }: GoToItemOptions = {}) => {
    // A field's badge can point at an item the view leaves out; switch to a view that lists it so its card shows.
    if (!keepView && !isListed(item)) dispatch({ type: "setReviewView", view: viewListing(item) });
    if (item.__typename === "ReportSuggestionType") {
      const fieldId = anchorOf(item);
      if (!fieldId) return;
      dispatch({ type: "requestExpand", itemId: item.reportTarget?.id ?? null });
      dispatch({ type: "activate", fieldId, itemId: item.id });
      scrollToFieldIndicator(fieldId);
      return;
    }
    const fieldId = domAnchorOf(item) ?? "";
    dispatch({ type: "activate", fieldId, itemId: item.id });
    scrollToFieldIndicator(fieldId);
  };
};
